// Keep ads well separated from the publisher content that surrounds them.
export const GOOGLE_AD_INTERVAL = 8;

// Do not use display ads in full-screen stories/reels or other navigation-only
// screens. The only approved placement in this app is between feed posts.
export type GoogleAdPlacement = 'feed';

export const GOOGLE_ADSENSE_CLIENT =
  process.env.REACT_APP_GOOGLE_ADSENSE_CLIENT?.trim() ||
  'ca-pub-8783275730102947';

// AdMob usa IDs ca-app-pub e não funciona no navegador. Sem um slot web do
// AdSense configurado, o componente de anúncio deve permanecer desativado.
const DEFAULT_SLOT = process.env.REACT_APP_GOOGLE_ADSENSE_SLOT?.trim() || '';

export const GOOGLE_AD_SLOTS: Record<GoogleAdPlacement, string> = {
  feed: process.env.REACT_APP_GOOGLE_ADSENSE_FEED_SLOT?.trim() || DEFAULT_SLOT,
};

const warnedMissingSlots = new Set<GoogleAdPlacement>();
const validClient = /^ca-pub-\d+$/.test(GOOGLE_ADSENSE_CLIENT);

export const isGoogleAdConfigured = (placement: GoogleAdPlacement) => {
  const slot = GOOGLE_AD_SLOTS[placement];
  const configured = validClient && /^\d+$/.test(slot);

  if (
    !configured &&
    process.env.NODE_ENV !== 'test' &&
    !warnedMissingSlots.has(placement)
  ) {
    warnedMissingSlots.add(placement);
    console.warn(
      `[GoogleAds] Configuração inválida para "${placement}". ` +
        'Use um client ca-pub-... e um slot numérico criado no AdSense (não AdMob).',
    );
  }

  return configured;
};

const ADSENSE_SCRIPT_SELECTOR =
  'script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]';

let googleAdsScriptPromise: Promise<void> | null = null;

export function loadGoogleAdsScript(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (window.adsbygoogle) return Promise.resolve();
  if (googleAdsScriptPromise) return googleAdsScriptPromise;

  googleAdsScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      ADSENSE_SCRIPT_SELECTOR,
    );
    const script = existing ?? document.createElement('script');
    let loadTimer = 0;

    const cleanup = () => {
      window.clearTimeout(loadTimer);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
    const handleLoad = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      googleAdsScriptPromise = null;
      reject(new Error('Falha ao carregar o Google AdSense.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    loadTimer = window.setTimeout(handleError, 10_000);

    if (!existing) {
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${GOOGLE_ADSENSE_CLIENT}`;
      document.head.appendChild(script);
    }
  });

  return googleAdsScriptPromise;
}
